import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Link as MuiLink,
  useTheme
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';

const Login = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      await login(data.username, data.password);
      const redirectTo =
        location.state?.from ||
        localStorage.getItem('redirectAfterLogin') ||
        '/';
      localStorage.removeItem('redirectAfterLogin');
      navigate(redirectTo, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="80vh"
      p={2}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          backgroundColor: colors.grey[800]
        }}
      >
        <Typography variant="h3" fontWeight="bold" mb={3} textAlign="center">
          {t('login')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label={t('email')}
            type="email"
            margin="normal"
            {...register('username', { 
              required: t('emailRequired'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('invalidEmail')
              }
            })}
            error={!!errors.username}
            helperText={errors.username?.message}
            disabled={loading}
          />

          <TextField
            fullWidth
            label={t('password')}
            type="password"
            margin="normal"
            {...register('password', { required: t('passwordRequired') })}
            error={!!errors.password}
            helperText={errors.password?.message}
            disabled={loading}
          />

          <Box textAlign="right" mt={1}>
            <MuiLink
              component={Link}
              to="/forgot-password"
              sx={{
                color: colors.blueAccent[400],
                textDecoration: 'none',
                fontSize: '0.875rem'
              }}
            >
              {t('forgotPassword') || 'Forgot Password'}?
            </MuiLink>
          </Box>

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            {loading ? t('loggingIn') : t('login')}
          </Button>

          <Box textAlign="center">
            <Typography variant="body2" color={colors.grey[300]}>
              {t('noAccount')}{' '}
              <MuiLink
                component={Link}
                to="/register"
                sx={{ color: colors.blueAccent[400], textDecoration: 'none' }}
              >
                {t('signUp')}
              </MuiLink>
            </Typography>
            <Typography variant="body2" color={colors.grey[300]} mt={1}>
              <MuiLink
                component={Link}
                to="/"
                sx={{ color: colors.blueAccent[400], textDecoration: 'none' }}
              >
                {t('continueAsGuest')}
              </MuiLink>
            </Typography>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default Login;
